/**
 * メールの確認まわりの判断（純関数・2026-09-28 creator/email-unverified）。画面は pages/VerifyEmail.tsx・components/EmailUnverified.tsx・
 * pages/AccountLoginMethods.tsx。テストは emailVerify.test.ts（.tsx は node で読めないので、判断はここに集める）。
 *
 * サーバーの正（Core・apps/server/src/modules/platform/recovery.ts・oauth/identities.ts・ownership.ts）:
 *   POST /v1/developers/email/verify { token }（kds_ を Bearer で付けられる）
 *     200 { ok: true, status: 'verified', invite? }              → 確認できた（invite = 招待クレジット・下の VerifyResponse）
 *     200 { ok: false, status: 'password_required', reason: 'no_session', resetToken: 'kdr_…', expiresAt }
 *         → セッションが無い（別の端末・別人・期限切れ）ので確認せず、「新しいパスワードを決める」流れ。
 *           サーバーはこの時点でそのアカウントのパスワード・全セッション・kdt_ を無効にしている（先回りの人が入り直せないように）
 *     400 invalid_token
 *   403 email_unverified: メールが未確認の開発者は sk_・kdt_・ログイン方法の追加・お金の口が使えない（SEC-DEVLOGIN-01）
 *   POST /v1/developers/me/password: パスワードの無い人は 403 password_reset_required（設定はメールの再設定の道だけ）
 */

export interface VerifyResponse {
  ok?: boolean;
  status?: string;
  reason?: string;
  resetToken?: string;
  expiresAt?: string;
  /**
   * 2026-09-30 core/invite-fields: 本人のセッションで今回確認できた応答（status: 'verified'）だけに
   * { credited, amountCents, currency } | null。読み方は inviteCode.ts の verifyInviteDecision（キーが無い = 古い本体）
   */
  invite?: unknown;
}

export type VerifyOutcome = { kind: 'verified' } | { kind: 'password_required'; resetToken: string; expiresAt: number | null };

/**
 * 応答 → 結果。status の無い以前の応答（{ ok: true }）は確認できた扱い。
 * password_required なのに resetToken が無い（形の食い違い）は、確認できたとは言えないので「パスワードを忘れた」へ回す（resetToken を空に）
 */
export function verifyOutcome(res: VerifyResponse | null | undefined): VerifyOutcome {
  if (res?.status === 'password_required') {
    const at = res.expiresAt ? Date.parse(res.expiresAt) : NaN;
    return { kind: 'password_required', resetToken: typeof res.resetToken === 'string' ? res.resetToken : '', expiresAt: Number.isFinite(at) ? at : null };
  }
  return { kind: 'verified' };
}

/**
 * /verify-email を開いた瞬間に自動で確認を送ってよいか = 手元に開発者のセッション（kds_）があるときだけ。
 * WHY（管理の判断 2026-09-28）: 会社のメールの安全装置（リンクの事前スキャン）が、届いたリンクを自動で開いて JavaScript まで動かすことがある。
 *   kds_ が無い状態で自動で送ると、本人が何もしないうちに password_required になり、パスワードが無効になる（サーバーの仕様）。
 *   kds_ があれば確認で終わる（パスワードは変わらない）ので、今までどおり自動でよい。無いときは本人がボタンを押してから送る
 */
export function shouldAutoVerify(sessionToken: string | null | undefined): boolean {
  return typeof sessionToken === 'string' && sessionToken.startsWith('kds_');
}

/**
 * 再設定のトークン（30 分）が切れたか。expiresAt が分からないときは切れていない扱い（サーバーが invalid_token で断れば同じ案内に回る）。
 * 送信の往復の間に切れるのを避けるため 15 秒の余裕を見る
 */
export function resetExpired(expiresAt: number | null, now: number): boolean {
  return expiresAt !== null && now >= expiresAt - 15_000;
}

type CodeError = { code?: string } | null | undefined;
export const isEmailUnverified = (err: CodeError) => err?.code === 'email_unverified';
export const isPasswordResetRequired = (err: CodeError) => err?.code === 'password_reset_required';

/**
 * /account のパスワードの行のボタン（2026-09-28 Core (B)）。
 *   change        = パスワードがある人の変更（今のパスワードの欄だけ。再認証のダイアログ・kdra_ は使わない = サーバーが受け付けない）
 *   sendResetLink = パスワードが無い人（Google などだけ）の設定は、登録メールに設定用のリンクを送る（POST /v1/developers/password/forgot）
 * WHY: kdra_（OAuth のやり直し）はダッシュボードの XSS からほぼ無操作で取れる。受信トレイは XSS では越えられない（Core の identities.ts）
 */
export function passwordAction(hasPassword: boolean): 'change' | 'sendResetLink' {
  return hasPassword ? 'change' : 'sendResetLink';
}
